/**
 * Step 3 "Azure Server Configuration" section for Apprise: the Azure portal "Create a
 * virtual machine" experience as tabs — Basics | Disks | Networking | Management |
 * Monitoring | Tags. (Wizard step 5 "Review & Deploy" is the "Review + create" tab.)
 * Rendered only when an Apprise product is selected; controlled by useAppriseVmForm.
 *
 * Tabs with problems carry a badge; Next (wizard-level) stays blocked until every tab is valid.
 */
import { useState } from 'react'
import { AlertCircle } from 'lucide-react'
import formStyles from '@/pages/NewDeploymentPage.module.css'
import { APPRISE_UNVERIFIED_NOTICE } from '@/utils/apprise'
import { Banner } from './AppriseFields'
import { AppriseBasicsTab } from './AppriseBasicsTab'
import { AppriseDisksTab } from './AppriseDisksTab'
import { AppriseNetworkingTab } from './AppriseNetworkingTab'
import { AppriseManagementTab } from './AppriseManagementTab'
import { AppriseMonitoringTab } from './AppriseMonitoringTab'
import { AppriseTagsTab } from './AppriseTagsTab'
import { APPRISE_TABS, adjacentTab, type AppriseTabId } from './appriseTabs'
import styles from './AppriseCreateVm.module.css'
import type { AppriseVmFormApi } from './useAppriseVmForm'
import clsx from 'clsx'

interface Props {
  form: AppriseVmFormApi
  sizeLabel: string
  /** Region changes go through the wizard's own form (azure_region). */
  onRegionChange: (region: string) => void
}

const tabLabel = (id: AppriseTabId) => APPRISE_TABS.find((t) => t.id === id)?.label ?? id

export function AppriseServerSection({ form, sizeLabel, onRegionChange }: Props) {
  const [tab, setTab] = useState<AppriseTabId>('basics')
  const existing = form.config.server.deployment_mode === 'use_existing'
  const tabs = existing ? APPRISE_TABS.filter((t) => t.id === 'basics') : APPRISE_TABS
  const active: AppriseTabId = tabs.some((t) => t.id === tab) ? tab : 'basics'
  const prev = adjacentTab(active, -1)
  const next = existing ? null : adjacentTab(active, 1)
  const problemTabs = tabs.filter((t) => form.tabErrors[t.id] > 0)

  return (
    <div className={formStyles.networkSection}>
      <h4 className={formStyles.networkSectionTitle}>Azure Server Configuration</h4>
      <p style={{ margin: '0 0 12px', fontSize: '0.8125rem', color: 'var(--aptean-text-secondary)' }}>
        Create a virtual machine in Azure — Microsoft Azure · Application SKU {sizeLabel}. This deployment creates the server only; it
        does not install Apprise.
        {form.catalogFallback && ' The size has no VM profile in the catalog yet — the standard defaults are used.'}
      </p>
      <div className={formStyles.formGrid} style={{ marginBottom: 8 }}>
        <Banner tone="warning">{form.options.notice || APPRISE_UNVERIFIED_NOTICE}</Banner>
      </div>

      <div role="tablist" aria-label="Create a virtual machine" className={styles.tabBar}>
        {tabs.map((t) => (
          <button key={t.id} type="button" role="tab" id={`ap-tab-${t.id}`} aria-selected={t.id === active} aria-controls={`ap-panel-${t.id}`}
            className={clsx(styles.tab, t.id === active && styles.tabActive)} onClick={() => setTab(t.id)}>
            {t.label}
            {form.tabErrors[t.id] > 0 && (
              <span className={styles.tabBadge} aria-label={`${form.tabErrors[t.id]} problem${form.tabErrors[t.id] === 1 ? '' : 's'}`}>{form.tabErrors[t.id]}</span>
            )}
          </button>
        ))}
      </div>

      <div role="tabpanel" id={`ap-panel-${active}`} aria-labelledby={`ap-tab-${active}`} className={styles.panel}>
        {active === 'basics' && <AppriseBasicsTab form={form} onRegionChange={onRegionChange} />}
        {active === 'disks' && <AppriseDisksTab form={form} />}
        {active === 'networking' && <AppriseNetworkingTab form={form} />}
        {active === 'management' && <AppriseManagementTab form={form} />}
        {active === 'monitoring' && <AppriseMonitoringTab form={form} />}
        {active === 'tags' && <AppriseTagsTab form={form} />}
      </div>

      {problemTabs.length > 0 && (
        <div className={formStyles.lbInfoBanner} role="alert" style={{ marginTop: 16, borderColor: 'rgba(224, 82, 82, 0.5)', background: 'rgba(224, 82, 82, 0.08)' }}>
          <AlertCircle size={13} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />
          Fix these before continuing:{' '}
          {problemTabs.map((t, i) => (
            <span key={t.id}>
              {i > 0 && ', '}
              <button type="button" className={styles.navBtn} style={{ padding: '1px 8px', marginLeft: 4 }} onClick={() => setTab(t.id)}>
                {t.label} ({form.tabErrors[t.id]})
              </button>
            </span>
          ))}
        </div>
      )}

      <div className={styles.tabNav}>
        {prev ? <button type="button" className={styles.navBtn} onClick={() => setTab(prev)}>&lt; Previous: {tabLabel(prev)}</button> : <span />}
        {next
          ? <button type="button" className={styles.navBtn} onClick={() => setTab(next)}>Next: {tabLabel(next)} &gt;</button>
          : <span className={styles.muted}>{existing ? '' : 'Review + create is the next wizard step — use Next below.'}</span>}
      </div>

      <div className={formStyles.lbInfoBanner} style={{ marginTop: 16 }}>
        <AlertCircle size={13} style={{ display: 'inline', marginRight: 6, verticalAlign: '-2px' }} />
        VM size availability, quota, image compatibility and Trusted Launch support are re-validated against Azure in the preflight job.
        {form.optionsFallback && ' (Platform options could not be loaded — showing defaults.)'}
      </div>
    </div>
  )
}
